import { apiClient } from './api';

export interface NavigationStats {
  incidents: number;
  executiveProtection: number;
  threats: number;
  investigations: number;
  subjects: number;
}

export interface IncidentStats {
  total: number;
  open: number;
  inProgress: number;
  resolved: number;
  critical: number;
}

export interface ExecutiveProtectionStats {
  total: number;
  active: number;
  onAssignment: number;
  highRisk: number;
}

export interface ThreatStats {
  total: number;
  active: number;
  monitored: number;
  critical: number;
}

export interface InvestigationStats {
  total: number;
  active: number;
  pending: number;
  closed: number;
  critical: number;
}

export interface SubjectStats {
  total: number;
  byRole: Record<string, number>;
  byRisk: Record<string, number>;
  createdInLast30Days: number;
}

class NavigationService {
  /**
   * Get navigation badge counts
   */
  async getNavigationStats(): Promise<NavigationStats> {
    try {
      // Fetch all stats in parallel
      const [incidentsRes, executiveProtectionRes, threatsRes, investigationsRes, subjectsRes] = await Promise.all([
        apiClient.get<IncidentStats>('/api/v1/incidents/stats').catch(() => ({ success: false, data: null })),
        apiClient.get<ExecutiveProtectionStats>('/api/v1/executive-protection/stats').catch(() => ({ success: false, data: null })),
        apiClient.get<ThreatStats>('/api/v1/threats/stats').catch(() => ({ success: false, data: null })),
        apiClient.get<InvestigationStats>('/api/v1/investigations/stats').catch(() => ({ success: false, data: null })),
        apiClient.get<SubjectStats>('/api/v1/subjects/stats').catch(() => ({ success: false, data: null })),
      ]);

      return {
        // Show count of open+in-progress incidents
        incidents: (incidentsRes.data?.open ?? 0) + (incidentsRes.data?.inProgress ?? 0),
        // Show count of high-risk executive protection items
        executiveProtection: executiveProtectionRes.data?.highRisk ?? 0,
        // Show count of active+critical threats
        threats: (threatsRes.data?.active ?? 0) + (threatsRes.data?.critical ?? 0),
        // Show count of active investigations
        investigations: investigationsRes.data?.active ?? 0,
        // Show total count of subjects
        subjects: subjectsRes.data?.total ?? 0,
      };
    } catch (error) {
      console.error('Failed to fetch navigation stats:', error);
      // Return zeros on error
      return {
        incidents: 0,
        executiveProtection: 0,
        threats: 0,
        investigations: 0,
        subjects: 0,
      };
    }
  }

  /**
   * Get detailed incident stats
   */
  async getIncidentStats(): Promise<IncidentStats | null> {
    const response = await apiClient.get<IncidentStats>('/api/v1/incidents/stats');
    return response.success && response.data ? response.data : null;
  }

  /**
   * Get detailed executive protection stats
   */
  async getExecutiveProtectionStats(): Promise<ExecutiveProtectionStats | null> {
    const response = await apiClient.get<ExecutiveProtectionStats>('/api/v1/executive-protection/stats');
    return response.success && response.data ? response.data : null;
  }

  /**
   * Get detailed threat stats
   */
  async getThreatStats(): Promise<ThreatStats | null> {
    const response = await apiClient.get<ThreatStats>('/api/v1/threats/stats');
    return response.success && response.data ? response.data : null;
  }

  /**
   * Get detailed investigation stats
   */
  async getInvestigationStats(): Promise<InvestigationStats | null> {
    const response = await apiClient.get<InvestigationStats>('/api/v1/investigations/stats');
    return response.success && response.data ? response.data : null;
  }

  /**
   * Get detailed subject stats
   */
  async getSubjectStats(): Promise<SubjectStats | null> {
    const response = await apiClient.get<SubjectStats>('/api/v1/subjects/stats');
    return response.success && response.data ? response.data : null;
  }
}

export const navigationService = new NavigationService();
